/**
 * TrialBanner — top-of-screen pill for anonymous visitors. Shows the
 * remaining trial seconds with a live countdown driven from /api/me +
 * a local interval. At ≤30s remaining, copy softens to a sign-up nudge.
 *
 * Props:
 *   - me        : the latest /api/me payload (anon scope)
 *   - active    : true while a voice call is in progress (counts down)
 *   - onSignup  : callback when user clicks "Sign up" link in the banner
 */
import { useEffect, useState } from "react";

function fmt(s) {
  const total = Math.max(0, Math.round(s));
  const m = Math.floor(total / 60);
  const r = total % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function TrialBanner({ me, active, onSignup }) {
  const baseRemaining = Number(me?.voice_seconds_remaining ?? 0);
  const [remaining, setRemaining] = useState(baseRemaining);

  // Snap to whatever /api/me said.
  useEffect(() => { setRemaining(baseRemaining); }, [baseRemaining]);

  // While the call is active, decrement locally so the UI stays smooth
  // between server pings.
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!me || me.scope !== "anon") return null;

  const lowWarning = remaining <= 30 && remaining > 0;

  return (
    <div
      data-testid="trial-banner"
      data-low={lowWarning ? "true" : "false"}
      className={
        "inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[12px] tracking-tight transition-colors " +
        (lowWarning
          ? "border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/10 text-foreground"
          : "border-border/70 bg-card/70 text-muted-foreground backdrop-blur")
      }
    >
      <span
        className={
          "inline-block h-1.5 w-1.5 rounded-full " +
          (lowWarning ? "bg-[hsl(var(--accent))] animate-pulse" : "bg-muted-foreground/40")
        }
        aria-hidden
      />
      <span>
        <span className="font-medium" data-testid="trial-banner-time">{fmt(remaining)}</span>
        <span className="text-muted-foreground/70"> free</span>
      </span>
      <button
        type="button"
        onClick={onSignup}
        data-testid="trial-banner-signup"
        className="text-[12px] text-muted-foreground/80 underline-offset-4 transition hover:text-foreground hover:underline"
      >
        Sign up to keep talking
      </button>
    </div>
  );
}
